import { useTranslation } from "react-i18next";
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import Popover from '@mui/material/Popover';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

export const RequestPopover = (props) => {
  const { anchorEl, onClose, open, users } = props;
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);

  // Load notifications from props when available
  useEffect(() => {
    if(users) setNotifications(users);
  }, [users]);

  // Group notifications by file (unique combination of user, project and file)
  // We now include all processing requests regardless of their done state.
  const groupedNotifications = useMemo(() => {
    const groups = {};
    notifications.forEach(user => {
      const userEmail = user.email;
      const projects = user.projects || [];
      projects.forEach(project => {
        const projectTitle = project.title;
        const dataItems = project.data || [];
        dataItems.forEach(dataItem => {
          const processingItems = dataItem.processing_requests || [];
          if (processingItems.length > 0) {
            const key = `${userEmail}|${projectTitle}|${dataItem.filename}`;
            if (!groups[key]) {
              groups[key] = {
                userName: userEmail,
                projectName: projectTitle,
                fileName: dataItem.filename,
                processings: [],
              };
            }
            processingItems.forEach(pr => {
              if(!pr.done){
                groups[key].processings.push({
                  id: pr.id,
                  processingName: pr.processing.title,
                  done: pr.done,
                });
              }
            });
          }
        });
      });
    });
    return Object.values(groups);
  }, [notifications]);

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      onClose={onClose}
      open={open}
      slotProps={{ paper: { sx: { mt: 1, width: 400 } } }}
    >
      <List>
        {groupedNotifications.length > 0 ? (
          groupedNotifications.map((group, index) => (
            <div key={index}>
              <ListItem>
                <Stack sx={{ p: 1, width: '100%' }} spacing={1}>
                  {/* Display group header with user, project and file info */}
                  <Typography variant="body1">
                    <b>{group.userName}</b>
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{justifyContent: "flex-start",alignItems: "center"}}>
                    <Typography variant="overline">
                      {t("Project")}: {group.projectName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("File")}: {group.fileName}
                    </Typography>
                  </Stack>
                  {/* Render a checkbox for each processing */}
                  {group.processings.map((proc) => (
                    <Stack key={proc.id} >
                      <Typography variant="body2">
                        {t(proc.processingName)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </ListItem>
              {index < groupedNotifications.length - 1 && <Divider />}
            </div>
          ))
        ) : (
          <ListItem>
            <ListItemText primary={t("No more notifications")} />
          </ListItem>
        )}
      </List>
    </Popover>
  );
};

RequestPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};
